"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

export default function Hero() {
  const [fontSize, setFontSize] = useState("14");
  const [animationDuration, setAnimationDuration] = useState(3);

  useEffect(() => {
    setFontSize(window.innerWidth > 1024 ? "18" : "16");
    setAnimationDuration(window.innerWidth > 768 ? 4 : 3);
  }, []);

  return (
    <section className="relative h-screen flex items-center overflow-hidden bg-[#040714]">
      {/* Circuit Board Pattern */}
      <svg className="absolute inset-0 w-full h-full z-0 opacity-20">
        <defs>
          <pattern id="circuitBoard" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M0 0h50v50H0z" fill="none" stroke="rgba(102, 16, 242, 0.3)" strokeWidth="0.5"/>
            <circle cx="25" cy="25" r="3" fill="rgba(23, 162, 184, 0.4)"/>
            <path d="M25 0v25M0 25h50" stroke="rgba(102, 16, 242, 0.3)" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#circuitBoard)"/>
      </svg>


      {/* Tech Nodes */}
      <svg className="absolute inset-0 w-full h-full z-2 opacity-30">
        <defs>
          <radialGradient id="nodeGlow">
            <stop offset="0%" stopColor="rgba(102, 16, 242, 0.4)"/>
            <stop offset="100%" stopColor="transparent"/>
          </radialGradient>
        </defs>
        <motion.g
          initial={{ scale: 0.8 }}
          animate={{ scale: 1.2 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        >
          <circle cx="20%" cy="30%" r="5" fill="url(#nodeGlow)"/>
          <circle cx="80%" cy="60%" r="5" fill="url(#nodeGlow)"/>
          <circle cx="40%" cy="80%" r="5" fill="url(#nodeGlow)"/>
        </motion.g>
      </svg>

      {/* Gradient Background */}
      <motion.div
        className="absolute inset-0 z-3"
        style={{
          background: `
            radial-gradient(
              circle at 30% 30%,
              rgba(102, 16, 242, 0.1) 0%,
              rgba(23, 162, 184, 0.1) 30%,
              transparent 70%
            )
          `,
          filter: 'blur(80px)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Rest of the content remains unchanged */}
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="max-w-3xl"
        >
          <motion.h1 
            className="text-3xl md:text-6xl font-bold mb-6 text-white"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            Transforming Ideas Into
            <motion.span 
              className="text-[#17A2B8] block mt-4 md:mt-none"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              Digital Excellence
            </motion.span>
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-300 mb-8 mt-6 md:mt-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            We deliver cutting-edge IT solutions that help businesses thrive in the digital age.
            From web development to cloud solutions, we're your partner in technological innovation.
          </motion.p>
          <motion.div 
            className="flex gap-4 mt-6 md:mt-none flex-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            <div className="w-[60%] sm:w-auto mb-2 sm:mb-0">
              <Link
                to="/contact"
                className="bg-primary-gradient text-white px-8 py-3 rounded-full 
                  hover:opacity-90 transition-all duration-300 
                  hover:scale-105 hover:shadow-lg hover:shadow-[#6610F2]/20
                  active:scale-95 hover:translate-y-[-2px] block text-center"
              >
                Get Started
              </Link>
            </div>
            <div className="w-[60%] sm:w-auto">
              <Link
                to="/portfolio"
                className="border-2 border-[#17A2B8] text-[#17A2B8] px-8 py-3 rounded-full 
                  hover:bg-[#17A2B8] hover:text-white transition-all duration-300 
                  hover:scale-105 hover:shadow-lg hover:shadow-[#17A2B8]/20
                  active:scale-95 hover:translate-y-[-2px] block text-center"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
      {/* Web Connection Lines */}
      <svg className="absolute inset-0 w-full h-full z-1 opacity-35">
        <defs>
          <linearGradient id="webGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(102, 16, 242, 0.5)"/>
            <stop offset="100%" stopColor="rgba(23, 162, 184, 0.5)"/>
          </linearGradient>
        </defs>
        <g>
          {/* Programming Languages Rain Animation */}
          <motion.g className="hidden md:block">
            {[
              "TypeScript", "React", "Angular", "HTML", "CSS", "Python", "Node.js", 
              "JavaScript", "Next.js", "Java", "MongoDB", "ExpressJS", "SQL", "Docker",
              "AWS"
            ].map((lang, index) => (
              <motion.text
                key={index}
                x={`${55 + Math.random() * 35}%`}
                y={`${20 + (index * 5)}%`}
                fill={Math.random() > 0.5 ? "rgba(137, 68, 247, 0.7)" : "rgba(23, 162, 184, 0.7)"}
                fontSize={fontSize}
                fontWeight="bold"
                fontFamily="monospace"
                initial={{ opacity: 0, y: -20 }}
                animate={{ 
                  opacity: [0.25, 1, 0],
                  y: ["-20%", "120%"]
                }}
                transition={{
                  duration: animationDuration,
                  delay: index * (animationDuration === 4 ? 0.7 : 0.5),
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                {fontSize === "16" && lang.length > 8 ? lang.substring(0, 8) + '..' : lang}
              </motion.text>
            ))}
          </motion.g>

          {/* Mobile version with fewer languages */}
          <motion.g className="block md:hidden">
            {[
              "TypeScript", "React", "Angular", "HTML", "CSS", "Python", "Node.js", 
              "JavaScript", "Next.js", "Java", "MongoDB", "ExpressJS", "SQL", "Docker",
              "AWS"
            ].map((lang, index) => (
              <motion.text
                key={index}
                x={`${50 + Math.random() * 40}%`}
                y={`${30 + (index * 8)}%`}
                fill={Math.random() > 0.5 ? "rgba(137, 68, 247, 0.7)" : "rgba(23, 162, 184, 0.7)"}
                fontSize="14"
                fontWeight="bold"
                fontFamily="monospace"
                initial={{ opacity: 0, y: -20 }}
                animate={{ 
                  opacity: [0.25, 1, 0],
                  y: ["-20%", "120%"]
                }}
                transition={{
                  duration: 3,
                  delay: index * 0.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                {lang}
              </motion.text>
            ))}
          </motion.g>
        </g>
      </svg>
    </section>
  );
}